import { useEffect, useRef, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronDown, Download, RefreshCw, Search, Trash2, FileText } from 'lucide-react'
import { useShipment } from '@/hooks/useShipments'
import { AppIcon } from '@/components/ui/app-icon'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { formatDateTime } from '@/lib/utils'
import { useToast } from '@/hooks/useToast'
import { shipmentsApi } from '@/lib/api'
import type { ShipmentItemType } from '@/types'

const TYPE_LABELS: Record<ShipmentItemType, string> = {
  km: 'КМ',
  box: 'Коробка',
  pallet: 'Паллета',
}

export function ShipmentDetailPage() {
  const { shipmentId } = useParams<{ shipmentId: string }>()
  const navigate = useNavigate()
  const { data: shipment, isLoading, refetch } = useShipment(shipmentId!)
  const { toast } = useToast()

  const [codeInput, setCodeInput] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [searchResult, setSearchResult] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null)
  const [exportMenuOpen, setExportMenuOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const exportMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!exportMenuOpen) return
    const onPointerDown = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [exportMenuOpen])

  if (isLoading || !shipment) return <LoadingSpinner className="mt-20" />

  const additions = shipment.additions ?? []
  const additionsCount = additions.length
  const palletsCount = additions.reduce((sum, a) => sum + (a.pallets_count || 0), 0)
  const boxesCount = additions.reduce((sum, a) => sum + (a.boxes_count || 0), 0)
  const kmCount = additions.reduce((sum, a) => sum + (a.km_count || 0), 0)

  const handleExport = async (format: 'xlsx' | 'txt') => {
    setIsExporting(true)
    setExportMenuOpen(false)
    try {
      const response = format === 'xlsx'
        ? await shipmentsApi.exportExcel(shipmentId!)
        : await shipmentsApi.exportTxt(shipmentId!)
      const blob = new Blob(
        [response.data as BlobPart],
        format === 'xlsx'
          ? { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
          : { type: 'text/plain;charset=utf-8' },
      )
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `shipment_${shipmentId}.${format}`
      a.click()
      URL.revokeObjectURL(url)
      toast.success(format === 'xlsx' ? 'Экспорт в Excel завершён' : 'TXT экспортирован')
      refetch()
    } catch {
      toast.error(format === 'xlsx' ? 'Ошибка экспорта Excel' : 'Ошибка экспорта TXT')
    } finally {
      setIsExporting(false)
    }
  }

  const handleStatusChange = async (newStatus: string) => {
    try {
      await shipmentsApi.updateStatus(shipmentId!, newStatus)
      toast.success(newStatus === 'completed' ? 'Отгрузка завершена — коды переведены в статус «отгружен»' : 'Статус обновлён')
      refetch()
    } catch (err: unknown) {
      const resp = (err as { response?: { data?: { error?: string } } })?.response?.data
      toast.error(resp?.error || 'Ошибка обновления статуса')
    }
  }

  const handleAddCode = async () => {
    const code = codeInput.trim()
    if (!code) {
      toast.error('Введите код')
      return
    }

    setIsSearching(true)
    setSearchResult(null)

    try {
      const { data } = await shipmentsApi.searchCode(code)

      if (!data.found) {
        setSearchResult({ type: 'error', message: data.message || 'Код не найден' })
        return
      }

      const exists = additions.some(a => a.code === code || a.code === data.code)
      if (exists) {
        setSearchResult({ type: 'warning', message: 'Дубликат! Этот код уже добавлен в отгрузку' })
        return
      }

      await shipmentsApi.addItem(shipmentId!, code)

      const preview = data.preview
      const typeLabel = data.type ? TYPE_LABELS[data.type] : 'код'
      setSearchResult({
        type: 'success',
        message: `${typeLabel} добавлен. Задание: ${data.task_display_id || data.task_id}${preview ? ` → КМ: ${preview.km}, коробок: ${preview.boxes}, паллет: ${preview.pallets}` : ''}`,
      })
      setCodeInput('')
      refetch()
    } catch (err: unknown) {
      const resp = (err as { response?: { data?: { error?: string } } })?.response?.data
      setSearchResult({ type: 'error', message: resp?.error || (err instanceof Error ? err.message : 'Ошибка добавления') })
    } finally {
      setIsSearching(false)
    }
  }

  const handleRemoveAddition = async (additionId: string) => {
    if (!confirm('Удалить позицию и весь развёрнутый состав из отгрузки?')) return
    try {
      await shipmentsApi.removeItem(shipmentId!, additionId)
      toast.success('Позиция удалена')
      refetch()
    } catch {
      toast.error('Ошибка удаления')
    }
  }

  const handleDeleteShipment = async () => {
    if (!shipmentId) return
    if (!confirm(`Удалить отгрузку ${shipmentId}?`)) return
    setIsDeleting(true)
    try {
      await shipmentsApi.delete(shipmentId)
      toast.success('Отгрузка удалена')
      navigate('/admin/shipments')
    } catch {
      toast.error('Ошибка удаления отгрузки')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin/shipments">
          <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4" /></Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Отгрузка {shipment.id}</h1>
          <p className="text-sm text-gray-500">
            Создал: {shipment.created_by} &middot; {formatDateTime(shipment.created_at)}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4" />
        </Button>
        {shipment.status === 'draft' && (
          <Button variant="ghost" size="sm" onClick={handleDeleteShipment} isLoading={isDeleting} className="text-red-500 hover:text-red-700">
            <Trash2 className="w-4 h-4 mr-1" /> Удалить
          </Button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-5 mb-6">
        <div className="grid grid-cols-3 gap-4 text-sm mb-4">
          <div>
            <span className="text-gray-500">Дата создания:</span>
            <p className="font-medium">{formatDateTime(shipment.created_at)}</p>
          </div>
          <div>
            <span className="text-gray-500">Создал:</span>
            <p className="font-medium">{shipment.created_by}</p>
          </div>
          <div>
            <span className="text-gray-500">Статус:</span>
            <select
              className="mt-1 w-full border rounded-md px-2 py-1.5 text-sm"
              value={shipment.status}
              onChange={e => handleStatusChange(e.target.value)}
            >
              <option value="draft">Черновик</option>
              <option value="completed">Завершена</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4 text-center border-t pt-4">
          <div>
            <p className="text-gray-500 text-xs">Позиций</p>
            <p className="text-2xl font-bold text-gray-800">{additionsCount}</p>
            <p className="text-xs text-gray-400">{shipment.items_count} в реестре</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs inline-flex items-center gap-1 justify-center"><AppIcon name="pallet" className="h-3.5 w-3.5" /> Паллет</p>
            <p className="text-2xl font-bold text-blue-600">{palletsCount}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs inline-flex items-center gap-1 justify-center"><AppIcon name="box" className="h-3.5 w-3.5" /> Коробок</p>
            <p className="text-2xl font-bold text-cyan-600">{boxesCount}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs inline-flex items-center gap-1 justify-center"><AppIcon name="labels" className="h-3.5 w-3.5" /> КМ</p>
            <p className="text-2xl font-bold text-green-600">{kmCount}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-6 items-center">
        <div className="relative" ref={exportMenuRef}>
          <Button
            onClick={() => setExportMenuOpen(open => !open)}
            isLoading={isExporting}
            aria-expanded={exportMenuOpen}
            aria-haspopup="menu"
          >
            <Download className="w-4 h-4" />
            Экспорт
            <ChevronDown className={`w-4 h-4 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
          </Button>
          {exportMenuOpen && (
            <div
              role="menu"
              className="absolute z-50 top-full left-0 mt-1 min-w-[220px] bg-white border border-gray-200 rounded-lg shadow-lg py-1"
            >
              <button
                type="button"
                role="menuitem"
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2"
                onClick={() => handleExport('xlsx')}
              >
                <Download className="w-4 h-4 text-green-600 shrink-0" />
                <span>Excel (.xlsx)</span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                onClick={() => handleExport('txt')}
                title="КМ: один код на строку"
              >
                <FileText className="w-4 h-4 text-gray-600 shrink-0" />
                <span>Текст (.txt)</span>
              </button>
            </div>
          )}
        </div>
        {shipment.exported && shipment.exported_at && (
          <p className="text-sm text-gray-500 self-center">Экспортировано: {formatDateTime(shipment.exported_at)}</p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-5 mb-6">
        <h2 className="font-semibold text-gray-900 mb-3">Добавить код</h2>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">КМ / КА коробки / КА паллеты:</label>
            <Input
              placeholder="КМ или (00)146207654100000185"
              value={codeInput}
              onChange={e => setCodeInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddCode()}
            />
          </div>
          <Button onClick={handleAddCode} isLoading={isSearching}>
            <Search className="w-4 h-4 mr-1" /> Найти и добавить
          </Button>
        </div>
        {searchResult && (
          <div className={`mt-3 rounded-lg p-3 text-sm ${
            searchResult.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
            searchResult.type === 'warning' ? 'bg-yellow-50 border border-yellow-200 text-yellow-700' :
            'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {searchResult.message}
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b">
          <h2 className="font-semibold text-gray-900">Позиции в отгрузке ({additionsCount})</h2>
        </div>
        {additions.length === 0 ? (
          <div className="text-center text-gray-500 py-10">
            Нет позиций. Добавьте коды выше.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-2">№</th>
                <th className="px-4 py-2">Тип</th>
                <th className="px-4 py-2">Код</th>
                <th className="px-4 py-2">GTIN</th>
                <th className="px-4 py-2">Задание</th>
                <th className="px-4 py-2">Состав</th>
                <th className="px-4 py-2">Добавлена</th>
                <th className="px-4 py-2">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {additions.map((a, i) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{i + 1}</td>
                  <td className="px-4 py-2">{TYPE_LABELS[a.added_as_type]}</td>
                  <td className="px-4 py-2 font-mono text-xs max-w-xs truncate" title={a.code}>{a.code}</td>
                  <td className="px-4 py-2 font-mono text-xs">{a.gtin}</td>
                  <td className="px-4 py-2 font-mono text-xs">{a.task_display_id || a.task_id}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">
                    {a.items_count} поз. (КМ: {a.km_count}, кор.: {a.boxes_count})
                  </td>
                  <td className="px-4 py-2 text-gray-500">{formatDateTime(a.added_at)}</td>
                  <td className="px-4 py-2">
                    {shipment.status === 'draft' && (
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveAddition(a.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
